import React, { Component } from 'react'
import { Form, Input, Select, Switch, Button, Skeleton, Alert } from 'antd'
import WrapperMappingData from './WrapperMappingData'
import { BreedProvider, BreedConsumer } from '../../../context/BreedContext'
import { SpecieProvider, SpecieConsumer } from '../../../context/SpecieContext'
import { SearchOptions } from '../../TableHelper'

// Forms
import MappingAgeForm from './Age'
import MappingAgeRangeForm from './AgeRange'
import MappingBreedForm from './Breed'
import MappingSexForm from './Sex'
import MappingSizeForm from './Size'
import MappingSpecieForm from './Specie'
import MappingCharacteristicForm from './Characteristic'

type MappingProps = {
  loadingContent: boolean,

  activeTab: string,
  uuid?: string,

  searchOptions: SearchOptions | null,

  partners: any | null,
  loadPartners: Function
}

export default class Mapping extends Component<MappingProps> {

  static defaultProps = {
    loadingContent: false,
    uuid: null
  }

  state = {
    loaded: false,
    saving: false
  }

  form: any
  formExtRef?: any

  static getDerivedStateFromProps(props: any, state: any) {
    // Keep our partners loaded
    if (!props.loadingContent && !props.partners) {
      state.loaded = false

      props.loadPartners({
        options: {
          limit: 9999
        }
      })
    }

    if (!props.loadingContent && props.partners && !state.loaded) {
      state.loaded = true
    }

    return state
  }

  handleSubmit = (values: any, dispatch: any) => {
    const { uuid } = this.props

    // Parse external id
    values = {
      ...values,
      external_id: values.external_id.toString()
    }

    this.setState({
      saving: true
    })
    dispatch({
      uuid,
      ...values
    })
  }

  renderForm = (activeTab: string) => {
    if (!this.form) return null

    switch (activeTab) {
      case 'age':
        return <MappingAgeForm />
      case 'age-range':
        return <MappingAgeRangeForm />
      case 'breed':
        return (
          <BreedProvider>
            <SpecieProvider>
              <BreedConsumer>
                {(breedContext) => (
                  <SpecieConsumer>
                    {(specieContext) => (
                      <MappingBreedForm form={this.form} breedContext={breedContext} specieContext={specieContext} />
                    )}
                  </SpecieConsumer>
                )}
              </BreedConsumer>
            </SpecieProvider>
          </BreedProvider>
        )
      case 'sex':
        return <MappingSexForm />
      case 'size':
        return <MappingSizeForm />
      case 'specie':
        return <MappingSpecieForm />
      case 'characteristic':
        return <MappingCharacteristicForm
          ref={ref => this.formExtRef = ref}
          form={this.form}
          partnerList={this.props.partners}
        />
    }
  }

  render() {
    const { activeTab, loadingContent, partners, uuid, searchOptions } = this.props
    const { saving } = this.state

    return (
      <WrapperMappingData activeTab={activeTab}>
        {({ values, loading, listMappings, loadMapping, saveMapping }: any) => {
          if ((loading && uuid && (!values || values.uuid !== uuid)) || (uuid && (!values || values.uuid !== uuid))) {
            // Load our entity
            if (!loading && uuid && (!values || values.uuid !== uuid)) {
              setTimeout(async () => {
                if (saving && searchOptions) {
                  await listMappings(searchOptions)
                }

                await loadMapping({
                  search: {
                    uuid
                  },
                  options: {
                    _force_ids: true
                  }
                })
              }, 500)
            }

            return <Skeleton />
          }

          const partnersOptions = partners?.map((partner: any) => {
            return {
              label: partner.name || partner.key,
              value: partner.key
            }
          }) as any

          const initialValues = uuid ? values : null
          const showCommonInputs = activeTab !== 'age-range' && activeTab !== "characteristic"
          return (
            <Form
              name={`mapping_${activeTab}`}
              layout="vertical"
              initialValues={initialValues}
              preserve={false}
              ref={(refs) => {
                if (!this.form) {
                  this.form = refs

                  // We need to force update the render, so we can get the valid form ref
                  this.forceUpdate()
                }
              }}
              onValuesChange={(changes, values) => {
                this.formExtRef?.onValuesChange(changes, values)
              }}
              onFinish={(values: any) => this.handleSubmit(values, saveMapping)}
            >
              {
                // Saved successfully
                !loading && saving ? (
                  <Alert
                    message="Saved"
                    description="Mapping saved successfully!"
                    type="success"
                    style={{ marginBottom: 15 }}
                    showIcon
                  />
                ) : null
              }

              {
                showCommonInputs ? (
                  <>
                    <Form.Item
                      label="Name"
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: 'Missing a name'
                        }
                      ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Partner"
                      name="partner_key"
                      rules={[
                        {
                          required: true,
                          message: 'Missing a partner'
                        }
                      ]}
                    >
                      <Select
                        loading={loadingContent}
                        placeholder="Select a partner"
                        options={partnersOptions}
                      />
                    </Form.Item>

                    <Form.Item
                      label="External ID"
                      name="external_id"
                      rules={[
                        {
                          required: true,
                          message: 'Missing an external id'
                        }
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </>
                ) : null
              }

              {
                this.renderForm(activeTab)
              }

              {
                showCommonInputs ? (
                  <Form.Item
                    label="Active"
                    name="active"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                ) : null
              }

              <Form.Item style={{ marginTop: 32 }}>
                <Button type="primary" htmlType="submit" loading={loading} size="large" block>
                  Save
                </Button>
              </Form.Item>
            </Form>
          )
        }}
      </WrapperMappingData>
    )
  }
}
